/**
 * Centralized Development Mode Utilities
 * 
 * Single source of truth for all dev mode detection in the frontend.
 * Uses build-time environment variables only - gets tree-shaken in production.
 * 
 * Usage Examples:
 * 
 * 1. Simple dev checks:
 *    import { IS_DEV } from './utils/devMode';
 *    if (IS_DEV) { console.log('Debug info'); }
 * 
 * 2. Conditional execution:
 *    import { devOnly } from './utils/devMode';
 *    devOnly(() => import('./debugUtils'));
 * 
 * 3. Conditional rendering:
 *    import { DevOnly } from './utils/devMode';
 *    <DevOnly><DebugPanel /></DevOnly>
 * 
 * 4. Feature flags:
 *    import { DEV_FEATURES } from './utils/devMode';
 *    if (DEV_FEATURES.adminAccess) { showAdminPanel(); }
 */

// ✅ SINGLE SOURCE OF TRUTH for dev mode detection
export const IS_DEV = process.env.NODE_ENV === 'development';
export const IS_PROD = process.env.NODE_ENV === 'production';

// ✅ Semantic utility functions (tree-shakable)
export const isDevelopment = (): boolean => IS_DEV;
export const isProduction = (): boolean => IS_PROD;

// ✅ Dev-only execution helper
export const devOnly = <T>(fn: () => T): T | void => {
  if (IS_DEV) return fn();
};

// ✅ Production-only execution helper  
export const prodOnly = <T>(fn: () => T): T | void => {
  if (IS_PROD) return fn();
};

// ✅ Conditional rendering helpers (for use in JSX)
// Note: These would be React components but keeping as utilities to avoid JSX in .ts file
export const shouldShowInDev = () => IS_DEV;
export const shouldShowInProd = () => IS_PROD;

// ✅ Build-time feature flags (gets optimized away in production)
export const DEV_FEATURES = {
  // Core dev mode features
  debugPanels: IS_DEV,
  factoryViews: IS_DEV,
  testAccounts: IS_DEV,
  verboseLogging: IS_DEV,
  devMenuItems: IS_DEV,
  
  // Admin features (requires explicit enable)
  adminAccess: IS_DEV && import.meta.env.VITE_DEV_ADMIN === 'true',
  
  // Debug features
  showDebugInfo: IS_DEV && import.meta.env.VITE_DEBUG_MODE === 'true',
  
  // Performance debugging
  showPerformanceMetrics: IS_DEV && import.meta.env.VITE_PERFORMANCE_DEBUG === 'true',
} as const;

// ✅ Environment info (for debugging and diagnostics)
export const ENV_INFO = {
  nodeEnv: process.env.NODE_ENV || 'unknown',
  isDev: IS_DEV,
  isProd: IS_PROD,
  buildTime: new Date().toISOString(),
  
  // Build-time feature detection
  hasReactDevTools: IS_DEV,
  hasHotReload: IS_DEV,
  hasSourceMaps: IS_DEV,
} as const;

// ✅ Development logging helpers (tree-shakable)
export const devLog = IS_DEV ? console.log.bind(console) : (() => {}) as any;
export const devWarn = IS_DEV ? console.warn.bind(console) : (() => {}) as any;
export const devError = IS_DEV ? console.error.bind(console) : (() => {}) as any;

// ✅ Assert function for development
export const devAssert = (condition: boolean, message: string): void => {
  if (IS_DEV && !condition) {
    throw new Error(`[DEV ASSERT] ${message}`);
  }
};

// ✅ Performance timing for development
export const devTime = (label: string): void => {
  if (IS_DEV) console.time(label);
};

export const devTimeEnd = (label: string): void => {
  if (IS_DEV) console.timeEnd(label);
};

// ✅ Development-only side effects
export const devEffect = (effect: () => void | (() => void)): (() => void) => {
  if (IS_DEV) {
    const cleanup = effect();
    return typeof cleanup === 'function' ? cleanup : () => {};
  }
  return () => {};
};

// ✅ Type guard for development
export const isInDevelopment = (): boolean => IS_DEV;

// Export for backward compatibility
export const isDev = IS_DEV;
export const isProd = IS_PROD;

// Hostname-based environment helpers
export const isLocalHostLike = (): boolean => {
  if (typeof window === 'undefined') return false;
  const hostname = window.location.hostname.toLowerCase();
  return (
    hostname === 'localhost' ||
    hostname === '127.0.0.1' ||
    hostname === '::1' ||
    hostname.endsWith('.local') ||
    hostname.startsWith('192.168.') ||
    hostname.startsWith('10.') ||
    (hostname.startsWith('172.') && (() => { const o = parseInt(hostname.split('.')[1]); return o >= 16 && o <= 31; })())
  );
};
